/**
 * Deal CRUD router
 * @package @app/api
 */

import { ORPCError } from '@orpc/server';
import { compensationItemSchema, deriveActivityCategoryFromLegacyType } from '@app/validators';
import { z } from 'zod';
import { captureServerEvent } from '../lib/analytics';
import { writeAuditLog } from '../lib/audit';
import {
  createDealCore,
  dealCreateInput,
  dealSourceEnum,
  isAwaitingAthleteConfirmation,
} from '../lib/deal-core';
import { protectedProcedure } from '../middleware/auth';
import { assertWithinDealLimit, requireWithinLimit } from '../middleware/feature-gate';

const dealUpdateInput = z.object({
  id: z.string(),
  title: z.string().min(1).max(200).optional(),
  description: z.string().optional(),
  brandName: z.string().min(1).optional(),
  brandContact: z.string().optional(),
  brandEmail: z.string().email().optional(),
  // US-028: pass null to clear, undefined to leave unchanged
  payorId: z.string().nullable().optional(),
  amount: z.number().nonnegative().optional(),
  status: z.enum(['DRAFT', 'ACTIVE', 'COMPLETED', 'EXPIRED', 'CANCELLED']).optional(),
  startDate: z.string().date().or(z.string().datetime()).optional(),
  endDate: z.string().date().or(z.string().datetime()).optional(),
  signedDate: z.string().date().or(z.string().datetime()).optional(),
  dealSource: dealSourceEnum.optional(),
  isExclusive: z.boolean().optional(),
  exclusivityTerms: z.string().optional(),
  notes: z.string().optional(),
  contractUrl: z.string().url().optional(),
  // Replace-all when supplied: server deletes existing items + recreates,
  // then re-syncs the legacy `amount` to the new sum. Omit to leave
  // compensation unchanged.
  compensationItems: z.array(compensationItemSchema).optional(),
});

export const dealRouter = {
  /**
   * Count deals that count against the per-plan `deals` quota — i.e. anything
   * not in a terminal status. Mirrors the count used by the server-side gate
   * on `create` so the client UI can pre-emptively redirect to /pricing
   * when the next create would be blocked.
   */
  activeCount: protectedProcedure.handler(async ({ context }) =>
    context.db.deal.count({
      where: {
        athleteId: context.session.user.id,
        status: { notIn: ['COMPLETED', 'EXPIRED', 'CANCELLED'] },
      },
    }),
  ),

  list: protectedProcedure
    .input(
      z.object({
        status: z.enum(['DRAFT', 'ACTIVE', 'COMPLETED', 'EXPIRED', 'CANCELLED']).optional(),
        page: z.number().int().positive().default(1),
        limit: z.number().int().positive().max(100).default(20),
      }),
    )
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const where: Record<string, unknown> = { athleteId: session.user.id };
      if (input.status) where.status = input.status;

      const [deals, total] = await Promise.all([
        db.deal.findMany({
          where,
          orderBy: { createdAt: 'desc' },
          skip: (input.page - 1) * input.limit,
          take: input.limit,
          include: {
            _count: { select: { payments: true, deliverables: true, files: true } },
          },
        }),
        db.deal.count({ where }),
      ]);

      return {
        deals: deals.map((d) => ({
          id: d.id,
          title: d.title,
          brandName: d.brandName,
          brandLogo: d.brandLogo,
          status: d.status,
          amount: Number(d.amount),
          currency: d.currency,
          startDate: d.startDate?.toISOString() ?? null,
          endDate: d.endDate?.toISOString() ?? null,
          createdAt: d.createdAt.toISOString(),
          aiExtracted: d.aiExtracted,
          paymentCount: d._count.payments,
          deliverableCount: d._count.deliverables,
          fileCount: d._count.files,
        })),
        total,
        page: input.page,
        totalPages: Math.ceil(total / input.limit),
      };
    }),

  get: protectedProcedure
    .input(z.object({ id: z.string() }))
    .handler(async ({ context, input }) => {
      const { db } = context;
      const deal = await db.deal.findFirst({
        where: { id: input.id, athleteId: context.session.user.id },
        include: {
          payments: { orderBy: { dueDate: 'asc' } },
          deliverables: {
            orderBy: { dueDate: 'asc' },
            include: { proofs: { orderBy: { capturedAt: 'desc' } } },
          },
          files: { orderBy: { createdAt: 'desc' } },
          // US-029: structured compensation items, ordered by athlete's
          // chosen orderIndex.
          compensationItems: { orderBy: { orderIndex: 'asc' } },
          // US-028 + edit form: payor so the detail page can render the
          // current selection and the edit form can pre-populate the
          // typeahead.
          payor: true,
        },
      });
      if (!deal) throw new Error('Deal not found');

      return {
        ...deal,
        amount: Number(deal.amount),
        // Decimal doesn't survive the oRPC boundary as a number.
        commissionPercent:
          deal.commissionPercent === null ? null : Number(deal.commissionPercent),
        // D9: an agent created this and the athlete hasn't confirmed it
        // yet. Drives the confirm banner on the deal page.
        awaitingConfirmation: isAwaitingAthleteConfirmation(deal),
        createdAt: deal.createdAt.toISOString(),
        updatedAt: deal.updatedAt.toISOString(),
        startDate: deal.startDate?.toISOString() ?? null,
        endDate: deal.endDate?.toISOString() ?? null,
        signedDate: deal.signedDate?.toISOString() ?? null,
        payor: deal.payor
          ? {
              id: deal.payor.id,
              legalName: deal.payor.legalName,
              displayName: deal.payor.displayName,
              contactName: deal.payor.contactName,
              contactEmail: deal.payor.contactEmail,
              contactPhone: deal.payor.contactPhone,
              validationStatus: deal.payor.validationStatus,
              affiliation: deal.payor.affiliation,
            }
          : null,
        payments: deal.payments.map((p) => ({
          ...p,
          amount: Number(p.amount),
          dueDate: p.dueDate?.toISOString() ?? null,
          receivedDate: p.receivedDate?.toISOString() ?? null,
          createdAt: p.createdAt.toISOString(),
          updatedAt: p.updatedAt.toISOString(),
        })),
        deliverables: deal.deliverables.map((d) => ({
          ...d,
          dueDate: d.dueDate?.toISOString() ?? null,
          completedDate: d.completedDate?.toISOString() ?? null,
          createdAt: d.createdAt.toISOString(),
          updatedAt: d.updatedAt.toISOString(),
          // US-030: include proofs nested under each deliverable. capturedAt
          // is normalized to ISO string for serialization.
          proofs: (d.proofs ?? []).map((p) => ({
            id: p.id,
            type: p.type,
            url: p.url,
            notes: p.notes,
            capturedAt: p.capturedAt.toISOString(),
          })),
        })),
        files: deal.files.map((f) => ({
          ...f,
          createdAt: f.createdAt.toISOString(),
          updatedAt: f.updatedAt.toISOString(),
        })),
        // US-029: structured compensation items. Decimals are converted
        // to numbers at the boundary so the client never sees Prisma's
        // Decimal type. Royalty fields stay nullable for non-royalty
        // rows; the client knows how to interpret them by `type`.
        compensationItems: deal.compensationItems.map((c) => ({
          id: c.id,
          type: c.type,
          amount: Number(c.amount),
          currency: c.currency,
          description: c.description,
          unitLabel: c.unitLabel,
          royaltyPercent: c.royaltyPercent !== null ? Number(c.royaltyPercent) : null,
          expectedVolumeLow: c.expectedVolumeLow !== null ? Number(c.expectedVolumeLow) : null,
          expectedVolumeHigh: c.expectedVolumeHigh !== null ? Number(c.expectedVolumeHigh) : null,
          dueDate: c.dueDate?.toISOString() ?? null,
          orderIndex: c.orderIndex,
          createdAt: c.createdAt.toISOString(),
          updatedAt: c.updatedAt.toISOString(),
        })),
      };
    }),

  create: protectedProcedure
    // Count deals that aren't in a terminal state. A DRAFT counts because
    // the user could promote it to ACTIVE later, and otherwise the free
    // tier would have an unlimited "save as draft" loophole. COMPLETED /
    // EXPIRED / CANCELLED slots free up.
    .use(
      // Session is non-null after protectedProcedure; the Context type still
      // carries `session: Session | null` so we assert here.
      requireWithinLimit('deals', (ctx) =>
        // biome-ignore lint/style/noNonNullAssertion: chained after protectedProcedure
        ctx.db.deal.count({
          where: {
            athleteId: ctx.session!.user.id,
            status: { notIn: ['COMPLETED', 'EXPIRED', 'CANCELLED'] },
          },
        }),
      ),
    )
    .input(dealCreateInput)
    .handler(async ({ context, input }) => {
      // Self-serve: the actor and the owner are the same person, and no
      // agent provenance. `createDealCore` is byte-for-byte the previous
      // body under those arguments — see packages/api/src/lib/deal-core.ts.
      const userId = context.session.user.id;
      return createDealCore({
        db: context.db,
        actorId: userId,
        athleteId: userId,
        input,
      });
    }),


  update: protectedProcedure.input(dealUpdateInput).handler(async ({ context, input }) => {
    const { db } = context;
    // dealSource is accepted but not persisted — DB column doesn't exist yet.
    // compensationItems is pulled out — handled separately as a replace-all
    // after the deal row update.
    const { id, dealSource: _dealSource, compensationItems, ...data } = input;
    const userId = context.session.user.id;

    // Verify ownership
    const existing = await db.deal.findFirst({
      where: { id, athleteId: userId },
    });
    if (!existing) throw new Error('Deal not found');

    // Cap-bypass guard: re-check the active-deal limit when this update
    // transitions a deal from a terminal state (COMPLETED/EXPIRED/CANCELLED)
    // back to a non-terminal one (DRAFT/ACTIVE). Without this, a user at
    // their cap could complete a deal (count -1), create a new one (count
    // restored), then "un-complete" the old one — silently exceeding the
    // cap. The create middleware only runs on create; this is the missing
    // server-side gate. Admin tier bypasses via the helper.
    const TERMINAL_STATUSES = ['COMPLETED', 'EXPIRED', 'CANCELLED'] as const;
    const nextStatus = data.status as typeof existing.status | undefined;
    const wasTerminal = (TERMINAL_STATUSES as readonly string[]).includes(existing.status);
    const willBeNonTerminal =
      nextStatus !== undefined && !(TERMINAL_STATUSES as readonly string[]).includes(nextStatus);
    if (wasTerminal && willBeNonTerminal) {
      await assertWithinDealLimit(context, () =>
        db.deal.count({
          where: {
            athleteId: context.session.user.id,
            status: { notIn: ['COMPLETED', 'EXPIRED', 'CANCELLED'] },
          },
        }),
      );
    }

    // US-028: same payor access check as create. payorId === null clears
    // the link; undefined leaves it unchanged.
    if (data.payorId) {
      const payor = await db.payor.findUnique({ where: { id: data.payorId } });
      if (!payor) throw new Error('Payor not found');
      const ownsIt = payor.ownerId === userId;
      let orgShared = false;
      if (!ownsIt && payor.organizationId) {
        const m = await db.member.findFirst({
          where: { userId, organizationId: payor.organizationId },
        });
        orgShared = !!m;
      }
      if (!ownsIt && !orgShared) {
        throw new Error("You don't have access to that payor");
      }
      data.brandName = payor.legalName;
      if (payor.contactName && !data.brandContact) data.brandContact = payor.contactName;
      if (payor.contactEmail && !data.brandEmail) data.brandEmail = payor.contactEmail;
    }

    // When compensationItems is supplied, replace-all via nested
    // `deleteMany` + `create` (single round-trip, atomic in Prisma), and
    // re-sync the cached `amount` to the new sum so dashboards stay correct.
    const { createId } = await import('@app/db');
    const total = compensationItems
      ? compensationItems.reduce((s, it) => s + Number(it.amount || 0), 0)
      : undefined;
    const result = await db.deal.update({
      where: { id },
      data: {
        ...data,
        ...(total !== undefined ? { amount: total } : {}),
        startDate: data.startDate ? new Date(data.startDate) : undefined,
        endDate: data.endDate ? new Date(data.endDate) : undefined,
        signedDate: data.signedDate ? new Date(data.signedDate) : undefined,
        ...(compensationItems
          ? {
              compensationItems: {
                deleteMany: {},
                create: compensationItems.map((it, idx) => ({
                  id: createId(),
                  type: it.type,
                  amount: it.amount,
                  currency: it.currency ?? 'USD',
                  description: it.description,
                  unitLabel: it.unitLabel ?? null,
                  royaltyPercent: it.royaltyPercent ?? null,
                  expectedVolumeLow: it.expectedVolumeLow ?? null,
                  expectedVolumeHigh: it.expectedVolumeHigh ?? null,
                  dueDate: it.dueDate ? new Date(it.dueDate) : null,
                  orderIndex: idx,
                })),
              },
            }
          : {}),
      },
    });

    const statusChanged = 'status' in data && data.status !== existing.status;
    await captureServerEvent({
      distinctId: context.session.user.id,
      event: statusChanged ? 'deal_status_changed' : 'deal_updated',
      properties: {
        dealId: id,
        fieldsChanged: Object.keys(data),
        ...(statusChanged ? { previousStatus: existing.status, newStatus: data.status } : {}),
      },
    });

    return result;
  }),

  /**
   * Confirm an agent-created draft (D9).
   *
   * ATHLETE-ONLY BY CONSTRUCTION: the `where` scopes on
   * `athleteId: session.user.id`, so an agent calling this for their
   * client's deal simply finds nothing. That's the entire point of the
   * draft gate — the agent proposes, the athlete disposes. Do not
   * "helpfully" widen this to agency staff.
   *
   * Only applies to drafts an agent created; an athlete's own draft is
   * promoted through the normal status control.
   */
  confirmAgentDraft: protectedProcedure
    .input(z.object({ id: z.string() }))
    .handler(async ({ context, input }) => {
      const { db } = context;
      const deal = await db.deal.findFirst({
        where: { id: input.id, athleteId: context.session.user.id },
        select: { id: true, status: true, createdByAgentId: true, title: true },
      });
      if (!deal) throw new ORPCError('NOT_FOUND', { message: 'Deal not found' });
      if (!isAwaitingAthleteConfirmation(deal)) {
        throw new ORPCError('BAD_REQUEST', {
          message: 'This deal is not an agent-created draft awaiting your confirmation.',
        });
      }

      await db.deal.update({ where: { id: deal.id }, data: { status: 'ACTIVE' } });

      await writeAuditLog({
        action: 'deal.agent_draft_confirmed',
        category: 'agency',
        actor: {
          id: context.session.user.id,
          name: context.session.user.name,
          email: context.session.user.email,
          role: context.session.user.role ?? null,
        },
        target: { type: 'deal', id: deal.id, name: deal.title },
        context: { createdByAgentId: deal.createdByAgentId },
      });

      return { ok: true, status: 'ACTIVE' as const };
    }),

  delete: protectedProcedure
    .input(z.object({ id: z.string() }))
    .handler(async ({ context, input }) => {
      const { db } = context;
      const existing = await db.deal.findFirst({
        where: { id: input.id, athleteId: context.session.user.id },
      });
      if (!existing) throw new Error('Deal not found');

      await db.deal.delete({ where: { id: input.id } });

      await captureServerEvent({
        distinctId: context.session.user.id,
        event: 'deal_deleted',
        properties: { dealId: input.id },
      });

      return { success: true };
    }),

  addPayment: protectedProcedure
    .input(
      z.object({
        dealId: z.string(),
        amount: z.number().positive(),
        dueDate: z.string().optional(),
        method: z.string().optional(),
        notes: z.string().optional(),
      }),
    )
    .handler(async ({ context, input }) => {
      const { createId } = await import('@app/db');
      const { db } = context;
      const userId = context.session.user.id;

      const deal = await db.deal.findFirst({
        where: { id: input.dealId, athleteId: userId },
      });
      if (!deal) throw new Error('Deal not found');

      const payment = await db.payment.create({
        data: {
          id: createId(),
          amount: input.amount,
          dueDate: input.dueDate ? new Date(input.dueDate) : undefined,
          method: input.method,
          notes: input.notes,
          dealId: input.dealId,
          userId,
        },
      });

      await captureServerEvent({
        distinctId: userId,
        event: 'payment_recorded',
        properties: {
          paymentId: payment.id,
          dealId: input.dealId,
          amount: input.amount,
        },
      });

      return { id: payment.id };
    }),

  addDeliverable: protectedProcedure
    .input(
      z.object({
        dealId: z.string(),
        title: z.string(),
        description: z.string().optional(),
        type: z.string().optional(),
        platform: z.string().optional(),
        dueDate: z.string().optional(),
      }),
    )
    .handler(async ({ context, input }) => {
      const { createId } = await import('@app/db');
      const { db } = context;
      const userId = context.session.user.id;

      const deal = await db.deal.findFirst({
        where: { id: input.dealId, athleteId: userId },
      });
      if (!deal) throw new Error('Deal not found');

      const deliverable = await db.deliverable.create({
        data: {
          id: createId(),
          title: input.title,
          description: input.description,
          type: input.type,
          platform: input.platform,
          // US-030: auto-assign category for legacy callers so the new
          // detail page renders a sensible icon + readiness state.
          activityCategory: deriveActivityCategoryFromLegacyType(input.type),
          dueDate: input.dueDate ? new Date(input.dueDate) : undefined,
          dealId: input.dealId,
        },
      });

      return { id: deliverable.id };
    }),

  toggleDeliverable: protectedProcedure
    .input(z.object({ deliverableId: z.string() }))
    .handler(async ({ context, input }) => {
      const { db } = context;
      const deliverable = await db.deliverable.findUnique({
        where: { id: input.deliverableId },
        include: { deal: { select: { athleteId: true } } },
      });
      if (!deliverable || deliverable.deal.athleteId !== context.session.user.id) {
        throw new Error('Deliverable not found');
      }

      const newStatus = deliverable.status === 'COMPLETED' ? 'PENDING' : 'COMPLETED';
      await db.deliverable.update({
        where: { id: input.deliverableId },
        data: {
          status: newStatus,
          completedDate: newStatus === 'COMPLETED' ? new Date() : null,
        },
      });

      return { success: true };
    }),

  // Dashboard aggregation
  dashboard: protectedProcedure.handler(async ({ context }) => {
    const { db } = context;
    const userId = context.session.user.id;

    const [
      totalDeals,
      activeDeals,
      totalEarnings,
      pendingPayments,
      upcomingDeadlines,
      recentDeals,
    ] = await Promise.all([
      db.deal.count({ where: { athleteId: userId } }),
      db.deal.count({ where: { athleteId: userId, status: 'ACTIVE' } }),
      db.payment.aggregate({
        where: { userId, status: 'RECEIVED' },
        _sum: { amount: true },
      }),
      db.payment.aggregate({
        where: { userId, status: { in: ['PENDING', 'OVERDUE'] } },
        _sum: { amount: true },
      }),
      db.deliverable.findMany({
        where: {
          deal: { athleteId: userId },
          status: { in: ['PENDING', 'IN_PROGRESS'] },
          dueDate: { gte: new Date() },
        },
        orderBy: { dueDate: 'asc' },
        take: 5,
        include: {
          deal: { select: { id: true, title: true, brandName: true } },
        },
      }),
      db.deal.findMany({
        where: { athleteId: userId },
        orderBy: { createdAt: 'desc' },
        take: 5,
        select: {
          id: true,
          title: true,
          brandName: true,
          status: true,
          amount: true,
          createdAt: true,
        },
      }),
    ]);

    return {
      totalDeals,
      activeDeals,
      totalEarnings: Number(totalEarnings._sum.amount ?? 0),
      pendingPayments: Number(pendingPayments._sum.amount ?? 0),
      upcomingDeadlines: upcomingDeadlines.map((d) => ({
        id: d.id,
        title: d.title,
        dueDate: d.dueDate?.toISOString() ?? null,
        dealTitle: d.deal.title,
        brandName: d.deal.brandName,
        status: d.status,
      })),
      recentDeals: recentDeals.map((d) => ({
        ...d,
        amount: Number(d.amount),
        createdAt: d.createdAt.toISOString(),
      })),
    };
  }),
};
